import clsx from 'clsx';
import * as React from 'react';

import { GitBookUsers } from '@gitbook/api-client';
import {
    BLOCK_CODE,
    BLOCK_CODE_LINE,
    BLOCK_DIVIDER,
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_HEADING_3,
    BLOCK_LIST_ITEM,
    BLOCK_LIST_OL,
    BLOCK_LIST_UL,
    BLOCK_PARAGRAPH,
    BLOCK_QUOTE,
    BLOCK_TABLE,
    INLINE_LINK,
    type InlineLinkData,
    isDocumentEmpty,
    MARK_BOLD,
    MARK_CODE,
    MARK_ITALIC,
    MARK_STRIKETHROUGH,
    MARK_SUBSCRIPT,
    MARK_SUPERSCRIPT,
    table,
} from '@gitbook/doc-core';
import { Block, type Change, Inline, Mark, TextRange, type Value } from '@gitbook/slate';
import {
    type BlockProps,
    Editor,
    type InlineProps,
    type MarkProps,
    type NodeProps,
    type Plugin,
    useIsEditorComposing,
} from '@gitbook/slate-react';
import { Code, Divider } from '@gitbook/spine-react';
import { Logger } from '@gitbook/util-debug';
import { useEventCallback, useLiveRef, usePrevious } from '@gitbook/util-react-hooks';
import { isHttpUrl } from '@gitbook/util-url';

import { setKeyboardShortcutsScope } from '../../Keyboard';
import {
    createMentionPlugin,
    emojiPlugin,
    listPlugin,
    markdownBasicPlugins,
} from '../ContentEditor';
import { GitBookAgentAvatar } from '../Users';

const logger = Logger('app:basic-editor');

export interface BasicEditorRef {
    focus: (props?: { toEnd?: boolean }) => void;
}

export interface BasicEditorProps {
    /**
     * Value to dislay in the editor.
     */
    value: Value;
    /**
     * Callback when a change is made.
     */
    onChange: (change: Change) => void;
    /**
     * Callback when the editor is blurred.
     */
    onBlur?: () => void;
    /**
     * Callback when the user is submitting the content by pressing enter.
     * If none is passed, pressing enter will insert a new line.
     */
    onSubmit?: () => void;
    /**
     * Callback when the editor is focused.
     */
    onFocus?: () => void;
    /**
     * If true, the user can submit the content by pressing enter with the meta key.
     */
    submitWithMetaKey?: boolean;
    /**
     * If true, the editor will be focused when mounted.
     */
    autoFocus?: boolean;
    /**
     * Placeholder to display when the content is empty.
     */
    placeholder?: string;
    /**
     * Should the content be editable or not.
     */
    readOnly?: boolean;
    /**
     * Configuration for the type of mentions that can be inserted.
     */
    mentions?: Parameters<typeof createMentionPlugin>[0]['accept'];
    /**
     * Accept mentioning the GitBook agent.
     */
    acceptAgentMention?: boolean;
    /**
     * If true, the editor fills the rest of its flex container and scrolls what overflows it. Off
     * for a document read in a flow, which takes the height of its content.
     * @default true
     */
    scrollable?: boolean;
    /**
     * Class name to apply to the editor.
     */
    className?: string;
    /**
     * Class name to apply to the empty-state placeholder, e.g. to set its color. Defaults to the
     * muted text color.
     */
    placeholderClassName?: string;
    /**
     * Class name to use for typography.
     */
    typographyClassName?: string;
    /**
     * Callback when files are pasted into the editor (e.g. an image or a PDF).
     * When provided and the clipboard contains files, the default paste behavior is
     * prevented and the files are forwarded to this callback instead.
     */
    onPasteFiles?: (files: File[]) => void;
    /**
     * Plugins run ahead of the editor's own, e.g. to draw some blocks differently.
     */
    plugins?: Plugin[];
}

const defaultMentions: NonNullable<BasicEditorProps['mentions']> = {
    '#': ['page', 'space', 'user'],
    '@': ['user', 'page', 'space'],
};

/**
 * Basic editor component, used for comments and prompts.
 */
export function BasicEditor({
    ref,
    ...props
}: BasicEditorProps & { ref?: React.Ref<BasicEditorRef> }) {
    const {
        value,
        placeholder,
        autoFocus = false,
        readOnly = false,
        mentions = defaultMentions,
        acceptAgentMention = false,
        submitWithMetaKey = false,
        onSubmit,
        onPasteFiles,
        scrollable = true,
        className,
        placeholderClassName,
        typographyClassName = 'text-ui-action',
        plugins: extraPlugins,
    } = props;

    const onBlur = useEventCallback(props.onBlur ?? (() => {}));
    const onFocus = useEventCallback(props.onFocus ?? (() => {}));
    const onChange = useEventCallback(props.onChange);

    const submitPlugin = useOnSubmitPlugin(onSubmit, submitWithMetaKey);
    const pasteFilesPlugin = usePasteFilesPlugin(onPasteFiles);

    const plugins = React.useMemo(() => {
        return [
            ...(extraPlugins ?? []),
            { renderNode, renderMark },
            pasteFilesPlugin,
            emojiPlugin,
            createMentionPlugin({
                accept: mentions,
                extraGroupsAtStart: acceptAgentMention
                    ? [
                          (pick) => ({
                              key: 'agents',
                              label: 'GitBook Agent',
                              commands: [
                                  {
                                      key: 'agent',
                                      title: 'GitBook Agent',
                                      subtitle: 'Mention and get GitBook Agent to help you',
                                      icon: <GitBookAgentAvatar size="md" />,
                                      keywords: ['agent'],
                                      trigger: () => {
                                          pick({
                                              kind: 'user',
                                              user: GitBookUsers.GitbookAgent,
                                          });
                                      },
                                  },
                              ],
                          }),
                      ]
                    : [],
            }),
            listPlugin,
            ...markdownBasicPlugins,
            highlightUrlsPlugin,

            // Submit plugin should be at the end to not impact other plugins (ex: list)
            submitPlugin,
        ];
    }, [extraPlugins, submitPlugin, pasteFilesPlugin, acceptAgentMention, mentions]);

    React.useImperativeHandle<BasicEditorRef, BasicEditorRef>(
        ref,
        () => ({
            focus: (props?: { toEnd?: boolean }) => {
                try {
                    const change = value.change();

                    if (props?.toEnd) {
                        // Try to find the last block and position cursor at its end
                        const lastBlock = value.document.nodes.findLast((node) =>
                            Block.isBlock(node)
                        );
                        if (lastBlock) {
                            const lastText = lastBlock.getLastText();
                            if (lastText) {
                                onChange(change.collapseToEndOf(lastText).focus());
                                return;
                            }
                        }
                        // Fallback to end of document if we can't find specific nodes
                        onChange(change.collapseToEndOf(change.value.document).focus());
                    } else {
                        onChange(change.focus());
                    }
                } catch (_error) {
                    // If focusing fails, try with a fresh selection
                    try {
                        const firstBlock = value.document.nodes.first();
                        if (firstBlock && Block.isBlock(firstBlock)) {
                            const firstText = firstBlock.getFirstText();
                            if (firstText) {
                                onChange(value.change().collapseToStartOf(firstText).focus());
                                return;
                            }
                        }
                    } catch (_fallbackError) {
                        logger.error('Failed to focus editor', { _fallbackError, value });
                    }
                }
            },
        }),
        [value, onChange]
    );

    /**
     * Whenever the editor is focused, switch to the 'basic-editor' keyboard scope
     * (preventing any top-level shortcuts from conflicting with editing)
     */
    React.useEffect(() => {
        if (!value.isFocused) {
            return;
        }

        return setKeyboardShortcutsScope('basic-editor');
    }, [value.isFocused]);

    /**
     * When the editor is blurred/focused, we trigger the callbacks.
     */
    const { isBlurred, isFocused } = value;
    const wasBlurred = usePrevious(isBlurred);
    const wasFocused = usePrevious(isFocused);

    React.useEffect(() => {
        if (isBlurred === true && wasBlurred === false) {
            onBlur();
        }
    }, [wasBlurred, isBlurred, onBlur]);

    React.useEffect(() => {
        if (isFocused === true && wasFocused === false) {
            onFocus();
        }
    }, [wasFocused, isFocused, onFocus]);

    return (
        <div
            className={clsx(
                'relative flex flex-col',
                scrollable && 'flex-1 overflow-y-auto',
                typographyClassName,
                className
            )}
        >
            <Editor
                value={value}
                onChange={onChange}
                readOnly={readOnly}
                autoFocus={autoFocus}
                plugins={plugins}
                className="flex flex-col gap-2"
            >
                {placeholder ? (
                    <Placeholder
                        value={value}
                        placeholder={placeholder}
                        className={className}
                        placeholderClassName={placeholderClassName}
                    />
                ) : null}
            </Editor>
        </div>
    );
}

function Placeholder(props: {
    value: Value;
    placeholder: string;
    className?: string;
    placeholderClassName?: string;
}) {
    const { value, placeholder, className, placeholderClassName } = props;
    const isComposing = useIsEditorComposing();

    return isDocumentEmpty(value.document) && !isComposing ? (
        <div
            className={clsx(
                'pointer-events-none absolute inset-0',
                className,
                placeholderClassName ?? 'text-muted'
            )}
        >
            {placeholder}
        </div>
    ) : null;
}

/**
 * The blocks below `BLOCK_LIST_ITEM` only ever reach this renderer read-only, through
 * {@link BasicDocument}: the `display` schema accepts them, the `basic` schema the composer writes
 * with strips them. They are rendered compactly, on UI typography rather than page typography —
 * these documents live in a side panel, not on a page.
 */
function renderNode(props: NodeProps) {
    if (Block.isBlock(props.node) && !Inline.isInline(props.parent)) {
        switch (props.node.type) {
            case BLOCK_PARAGRAPH:
                return <p {...props.attributes}>{props.children}</p>;
            case BLOCK_LIST_UL:
                return (
                    <ul {...props.attributes} className="list-disc pl-5">
                        {props.children}
                    </ul>
                );
            case BLOCK_LIST_OL:
                return (
                    <ol {...props.attributes} className="list-decimal pl-5">
                        {props.children}
                    </ol>
                );
            case BLOCK_LIST_ITEM:
                return <li {...props.attributes}>{props.children}</li>;
            case BLOCK_HEADING_1:
                return (
                    <h1 {...props.attributes} className="mt-1 text-ui-heading-medium">
                        {props.children}
                    </h1>
                );
            case BLOCK_HEADING_2:
                return (
                    <h2 {...props.attributes} className="mt-1 text-ui-heading-small">
                        {props.children}
                    </h2>
                );
            case BLOCK_HEADING_3:
                return (
                    <h3 {...props.attributes} className="mt-1 text-ui-standout">
                        {props.children}
                    </h3>
                );
            case BLOCK_QUOTE:
                return (
                    <blockquote
                        {...props.attributes}
                        className="border-l-2 border-base pl-3 text-muted"
                    >
                        {props.children}
                    </blockquote>
                );
            case BLOCK_CODE:
                return (
                    <pre
                        {...props.attributes}
                        className="text-content-code overflow-x-auto rounded-sm bg-muted p-2 font-mono text-ui-small"
                    >
                        {props.children}
                    </pre>
                );
            case BLOCK_CODE_LINE:
                // A div rather than the pre's own text flow: each line is its own block in the
                // document, and inline elements would run them together on one line.
                return <div {...props.attributes}>{props.children}</div>;
            case BLOCK_DIVIDER:
                return (
                    <div {...props.attributes} className="py-1">
                        <Divider />
                        {props.children}
                    </div>
                );
            case BLOCK_TABLE:
                return <DisplayTable {...props} node={props.node} />;
        }
    }

    if (Inline.isInline(props.node)) {
        switch (props.node.type) {
            case INLINE_LINK:
                return <DisplayLink {...props} node={props.node} />;
        }
    }
}

/**
 * Read-only anchor for a link in a displayed document.
 *
 * Deliberately not the editor's `InlineLink`: that one resolves the link's content ref, which
 * throws without a `ContentReferenceContextProvider` above it — and the screens showing these
 * documents (Triage, Link audit) have none. A link a model wrote in Markdown carries a plain URL
 * and needs no resolution; a link to a page keeps its text and loses only its target, where the
 * basic schema used to replace the whole thing with a mention labelled by its destination.
 */
function DisplayLink(props: Pick<InlineProps, 'node' | 'attributes' | 'children'>) {
    const { node, attributes, children } = props;
    const { ref } = node.data.toFrozenObject<InlineLinkData>();

    // The URL comes out of a model's Markdown, so it is checked before it becomes an href.
    if (ref.kind !== 'url' || !isHttpUrl(ref.url)) {
        return <span {...attributes}>{children}</span>;
    }

    return (
        <a
            {...attributes}
            href={ref.url}
            target="_blank"
            rel="noreferrer"
            className="text-primary underline"
        >
            {children}
        </a>
    );
}

/**
 * Read-only text grid for a table block.
 *
 * A table is a void block holding its cells in its data, so the schema used to drop it whole,
 * taking every cell with it. Rendering the editor's own table would pull its interactive grid into
 * a side panel; the text of the cells is what a reader needs, and for a table a model wrote in
 * Markdown it is all the table ever held.
 */
function DisplayTable(props: Pick<BlockProps, 'node' | 'attributes' | 'children'>) {
    const { node, attributes, children } = props;

    const data = table.getTableData(node);
    const columns = data.view.columns;
    const headers = columns.map((column) => data.definition[column]?.title ?? '');
    const rows = table.defaultRecordSortedKeys(data).flatMap((key) => {
        const record = data.records[key];
        return record ? [{ key, cells: table.getTableRowPlainTexts(node, record) }] : [];
    });

    return (
        <div {...attributes} className="overflow-x-auto">
            <table className="w-full border-collapse text-left">
                {headers.some((title) => title !== '') ? (
                    <thead>
                        <tr>
                            {headers.map((title, index) => (
                                <th
                                    key={columns[index]}
                                    className="border border-base px-1.5 py-1 text-ui-standout"
                                >
                                    {title}
                                </th>
                            ))}
                        </tr>
                    </thead>
                ) : null}
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.key}>
                            {row.cells.map((cell, index) => (
                                <td key={columns[index]} className="border border-base px-1.5 py-1">
                                    {cell}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
            {children}
        </div>
    );
}

function renderMark(props: MarkProps) {
    switch (props.mark.type) {
        case MARK_BOLD:
            return (
                <strong {...props.attributes} className="font-bold">
                    {props.children}
                </strong>
            );
        case MARK_ITALIC:
            return (
                <i {...props.attributes} className="italic">
                    {props.children}
                </i>
            );
        case MARK_CODE:
            return <Code {...props.attributes}>{props.children}</Code>;
        case MARK_STRIKETHROUGH:
            return (
                <del {...props.attributes} className="line-through">
                    {props.children}
                </del>
            );
        case MARK_SUPERSCRIPT:
            return <sup {...props.attributes}>{props.children}</sup>;
        case MARK_SUBSCRIPT:
            return <sub {...props.attributes}>{props.children}</sub>;
    }
}

/** Plugin to trigger onSubmit when pressing enter (without shift) */
export function useOnSubmitPlugin(onSubmit: (() => void) | undefined, useMetaKey = false): Plugin {
    const onSubmitRef = useLiveRef(onSubmit);
    const hasSubmit = Boolean(onSubmit);

    return React.useMemo(() => {
        if (!hasSubmit) {
            return {};
        }

        return {
            onKeyDown: (event) => {
                const onSubmitKeysPressed = useMetaKey
                    ? event.key === 'Enter' && event.metaKey
                    : event.key === 'Enter';

                if (onSubmitKeysPressed && !event.shiftKey) {
                    onSubmitRef.current?.();
                    return true;
                }
            },
        };
    }, [hasSubmit, onSubmitRef, useMetaKey]);
}

/**
 * Plugin to forward pasted files (images, PDFs, …) to a callback instead of
 * letting the editor handle them. Returns truthy to mark the paste as handled
 * so the default text paste behavior is prevented.
 */
export function usePasteFilesPlugin(onPasteFiles: ((files: File[]) => void) | undefined): Plugin {
    const onPasteFilesRef = useLiveRef(onPasteFiles);
    const hasHandler = Boolean(onPasteFiles);

    return React.useMemo(() => {
        if (!hasHandler) {
            return {};
        }

        return {
            onPaste: (event) => {
                const files = Array.from(event.clipboardData?.files ?? []);
                if (files.length === 0) {
                    return;
                }

                onPasteFilesRef.current?.(files);
                return true;
            },
        };
    }, [hasHandler, onPasteFilesRef]);
}

/** Plugin to highlight raw URLs in the basic editor. */
const highlightUrlsPlugin: Plugin = {
    decorateNode: (node) => {
        if (node.object !== 'block') {
            return;
        }

        const decorations: TextRange[] = [];

        node.getTexts().forEach((textNode) => {
            const { text } = textNode;
            const urlRegex = /https?:\/\/[^\s]+/g;
            let match: RegExpExecArray | null = null;

            while ((match = urlRegex.exec(text))) {
                const [url] = match;
                const { index } = match;

                if (!isHttpUrl(url)) {
                    continue;
                }

                decorations.push(
                    TextRange.create({
                        anchorKey: textNode.key,
                        anchorOffset: index,
                        focusKey: textNode.key,
                        focusOffset: index + url.length,
                        marks: [
                            Mark.create({
                                type: 'url',
                                data: {
                                    url,
                                },
                            }),
                        ],
                    })
                );
            }
        });

        return decorations;
    },
    renderMark: (props) => {
        const { mark, children, editor } = props;

        if (mark.type === 'url') {
            const url = props.mark.data.get('url');
            const isEditing = !editor.readOnly && editor.value.isFocused;

            return (
                <a
                    {...props.attributes}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary underline"
                    onMouseDown={
                        !editor.readOnly && !isEditing
                            ? (event) => {
                                  event.preventDefault();
                              }
                            : undefined
                    }
                    onClick={
                        !editor.readOnly
                            ? (event) => {
                                  if (isEditing && !(event.metaKey || event.ctrlKey)) {
                                      event.preventDefault();
                                      return;
                                  }

                                  event.preventDefault();
                                  window.open(url, '_blank');
                              }
                            : undefined
                    }
                >
                    {children}
                </a>
            );
        }
    },
};
